import {
  createFileRoute,
  FileRouteTypes,
  Link,
  Outlet,
} from "@tanstack/react-router";
import {
  CommandLineIcon,
  ArrowRightStartOnRectangleIcon,
  AcademicCapIcon,
  BanknotesIcon,
  BookOpenIcon,
  ChartBarIcon,
  ArrowUpRightIcon,
  MapIcon,
  EnvelopeIcon,
  ShoppingCartIcon,
  KeyIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";

import { UserIcon } from "@heroicons/react/24/solid";

import { useLoginStatus } from "../../hooks/useLoginStatus";
import { LoginComponent } from "../../components/LoginComponent";
import { ChangeEvent, JSX, useEffect, useMemo, useRef, useState } from "react";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useUserSettingsUpdate } from "../../hooks/useUserSettingsUpdate";
import { useLogin } from "../../hooks/useLogin";
import useTranslation from "../../hooks/useTranslation";
import { notifySnackbar } from "../../lib/notifySnackbar";
import {
  BARKS_VIEW_PERMISSION,
  SHOPPING_LIST_VIEW_PERMISSION,
} from "../../permissions";
import { SnackBar } from "../../components/SnackBar";
import ProgressBar from "../../components/ProgressBar";
import { useUserProfileSendHeartbeat } from "../../hooks/useUserProfileSendHeartbeat";
import { useFrontendRefreshOnNewVersion } from "../../hooks/useFrontendRefreshOnNewVersion";
import {
  useLanguageSpace,
  type LanguageSpace,
} from "../../hooks/useLanguageSpace";
import { TEXT_LANGUAGES } from "../../constants";
import { useDailyTextCreate } from "../../hooks/useDailyTextCreate";
import { useUserProfileUpdate } from "../../hooks/useUserProfileUpdate";
import { useProfilePicPath } from "../../hooks/useProfilePicPath";
import { useNavigate, useRouterState } from "@tanstack/react-router";

export const Route = createFileRoute("/app")({
  component: AppLayout,
});

function AppLayout() {
  const loginStatus = useLoginStatus();

  if (!loginStatus.isSuccess) {
    return <></>;
  }

  if (loginStatus.data.state === "logged out") {
    return <LoginComponent />;
  }
  return <AppLayoutImpl />;
}

type LinkTarget = FileRouteTypes["to"];

interface NavItem {
  link: LinkTarget;
  text: string;
  icon: JSX.Element;
}

function AppLayoutImpl() {
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [commandStatus, setCommandStatus] = useState<{
    kind: "idle" | "pending" | "success" | "error";
    message?: string;
  }>({ kind: "idle" });
  const [profilePreviewUrl, setProfilePreviewUrl] = useState<string | null>(
    null,
  );
  const [selectedIntent, setSelectedIntent] =
    useState<CommandIntent>("HOME");
  const profilePickerRef = useRef<HTMLInputElement | null>(null);

  const loginStatus = useLoginStatus();
  const userSettings = useUserSettings();
  const updateUserSettings = useUserSettingsUpdate();
  const updateProfile = useUserProfileUpdate();
  const dailyTextCreate = useDailyTextCreate();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isOnMainScreen = pathname === "/app/reeprise";
  const isOnTextDetailsPage =
    pathname.startsWith("/app/reeprise/") && pathname !== "/app/reeprise";
  const hideHomeControl = navbarOpen;
  const hideNavbarAuxControls = navbarOpen || isOnTextDetailsPage;
  const homeVisibilityClass = hideHomeControl
    ? "opacity-0 pointer-events-none"
    : "opacity-100";
  const navbarAuxVisibilityClass = hideNavbarAuxControls
    ? "opacity-0 pointer-events-none"
    : "opacity-100";
  const profilePicPath = useProfilePicPath();
  useFrontendRefreshOnNewVersion();
  const { mutateAsync: sendHeartbeat } = useUserProfileSendHeartbeat();
  const login = useLogin();

  useEffect(() => {
    return () => {
      if (profilePreviewUrl) {
        URL.revokeObjectURL(profilePreviewUrl);
      }
    };
  }, [profilePreviewUrl]);

  const translation = useTranslation();
  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement | null>(null);
  const languageOptions = [
    ...TEXT_LANGUAGES.map((lang) => ({
      id: lang.id as LanguageSpace,
      label: translation.translate(lang.prettyTranslationKey),
      accentBg: lang.accentBg,
      accentText: lang.accentText,
    })),
    {
      id: "ALL" as LanguageSpace,
      label: translation.translate("language_space_all"),
      accentBg: "#e2e8f0",
      accentText: "#0f172a",
    },
  ];
  const currentLanguageIndex = languageOptions.findIndex(
    (opt) => opt.id === languageSpace,
  );
  const currentLanguage = languageOptions[currentLanguageIndex];
  const currentLanguageLabel =
    currentLanguage?.id === "GREEK_LATIN_TRANSCRIPTION"
      ? translation.translate("language_greek")
      : currentLanguage?.id === "RUSSIAN_LATIN_TRANSCRIPTION"
        ? translation.translate("language_russian")
      : currentLanguage?.id === "PORTUGUESE_FROM_BRAZIL" ||
          currentLanguage?.id === "PORTUGUESE_FROM_PORTUGAL"
        ? translation.translate("language_portuguese")
        : currentLanguage?.label ?? translation.translate("language_space_all");
  useEffect(() => {
    let stopped = false;
    (async () => {
      while (!stopped) {
        try {
          // only send the heartbeat if the document is visible
          if (document.visibilityState !== "visible") {
            await new Promise((resolve) => setTimeout(resolve, 5_000));
            continue;
          }
          await sendHeartbeat();
        } catch (error) {
          console.error("Failed to send heartbeat:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, 30_000));
      }
    })();
    return () => {
      stopped = true;
    };
  }, [sendHeartbeat]);

  useEffect(() => {
    setCommandStatus({ kind: "idle" });
  }, [commandQuery]);

  useEffect(() => {
    if (!languageMenuOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (!languageMenuRef.current) return;
      if (!languageMenuRef.current.contains(event.target as Node)) {
        setLanguageMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [languageMenuOpen]);

  useEffect(() => {
    if (hideNavbarAuxControls) {
      setLanguageMenuOpen(false);
    }
  }, [hideNavbarAuxControls]);

  if (loginStatus.data?.state !== "logged in") {
    return <div />;
  }

  const show_experimental =
    loginStatus.data.is_staff &&
    userSettings.data?.show_experimental_features === true;

  type CommandIntent =
    | "SET_UI_LANGUAGE"
    | "SET_MODEL"
    | "UPDATE_PROFILE_PIC"
    | "NAV_SETTINGS"
    | "START_DAILY_TEXT"
    | "LOGOUT"
    | "HOME"
    | "UNKNOWN";

  type CommandCandidate = {
    intent: CommandIntent;
    title: string;
    description?: string;
    confirmLabel?: string;
    onConfirm?: () => Promise<void> | void;
    options?: {
      label: string;
      onSelect: () => void;
      isSelected?: boolean;
    }[];
  };

  const languageAliasMap: Record<string, "ENGLISH" | "GERMAN"> = {
    english: "ENGLISH",
    englisch: "ENGLISH",
    german: "GERMAN",
    deutsch: "GERMAN",
  };

  const parseCommand = (input: string) => {
    const normalized = input.trim().toLowerCase();
    if (!normalized) return { intent: "UNKNOWN" as CommandIntent };
    if (/(einstellungen|settings|setting)/i.test(normalized)) {
      return { intent: "NAV_SETTINGS" as CommandIntent };
    }
    if (/(profil|profile|photo|bild|bild ändern|profile picture)/i.test(normalized)) {
      return { intent: "UPDATE_PROFILE_PIC" as CommandIntent };
    }
    if (/(text\s*f(ü|u)r\s*heute|daily\s*text|text\s*for\s*today)/i.test(normalized)) {
      return { intent: "START_DAILY_TEXT" as CommandIntent };
    }
    if (/(abmelden|logout|log\s*out|sign\s*out)/i.test(normalized)) {
      return { intent: "LOGOUT" as CommandIntent };
    }
    if (/(modell|model)/i.test(normalized)) {
      if (/(mini|4o\s*mini|gpt-4o-mini)/i.test(normalized)) {
        return { intent: "SET_MODEL" as CommandIntent, value: "gpt-4o-mini" };
      }
      if (/(4o|gpt-4o)/i.test(normalized)) {
        return { intent: "SET_MODEL" as CommandIntent, value: "gpt-4o" };
      }
      return { intent: "SET_MODEL" as CommandIntent };
    }
    if (/(sprache|language|app-sprache|ui\s*language)/i.test(normalized)) {
      const match = Object.keys(languageAliasMap).find((alias) =>
        normalized.includes(alias),
      );
      if (match) {
        return {
          intent: "SET_UI_LANGUAGE" as CommandIntent,
          value: languageAliasMap[match],
        };
      }
      return { intent: "SET_UI_LANGUAGE" as CommandIntent };
    }
    return { intent: "UNKNOWN" as CommandIntent };
  };

  const buildCandidate = (
    intent: CommandIntent,
    value?: string,
  ): CommandCandidate | null => {
    if (intent === "HOME" || intent === "UNKNOWN") {
      return null;
    }
    const setSuccess = (message: string) => {
      setCommandStatus({ kind: "success", message });
    };
    const setError = (message: string) => {
      setCommandStatus({ kind: "error", message });
    };
    const setPending = (message?: string) => {
      setCommandStatus({ kind: "pending", message });
    };
    if (intent === "NAV_SETTINGS") {
      return {
        intent: "NAV_SETTINGS",
        title: translation.translate("command_bar_item_settings"),
        confirmLabel: translation.translate("command_bar_action_settings"),
        onConfirm: () => {
          navigate({ to: "/app/settings" });
          setNavbarOpen(false);
        },
      };
    }
    if (intent === "START_DAILY_TEXT") {
      return {
        intent: "START_DAILY_TEXT",
        title: translation.translate("command_bar_confirm_daily"),
        confirmLabel: translation.translate("command_bar_action_daily"),
        onConfirm: async () => {
          try {
            setPending(translation.translate("command_bar_status_working"));
            const created = await dailyTextCreate.mutateAsync({
              language: languageSpace === "ALL" ? "ENGLISH" : languageSpace,
            });
            setSuccess(translation.translate("command_bar_status_done"));
            if (created?.id) {
              navigate({
                to: "/app/reeprise/$generatedTextId",
                params: { generatedTextId: `${created.id}` },
                search: { chapter: undefined },
              });
            }
            setNavbarOpen(false);
          } catch {
            setError(translation.translate("command_bar_status_error"));
          }
        },
      };
    }
    if (intent === "SET_UI_LANGUAGE") {
      const applyLanguage = async (value: "ENGLISH" | "GERMAN") => {
        try {
          setPending(translation.translate("command_bar_status_working"));
          await updateUserSettings.mutateAsync({
            userSettingsDto: { app_language: value },
          });
          setSuccess(translation.translate("command_bar_status_done"));
        } catch {
          setError(translation.translate("command_bar_status_error"));
        }
      };
      const castValue = value as "ENGLISH" | "GERMAN" | undefined;
      return {
        intent: "SET_UI_LANGUAGE",
        title: castValue
          ? translation.translate("command_bar_confirm_language", {
              language:
                castValue === "ENGLISH"
                  ? translation.translate("command_bar_language_english")
                  : translation.translate("command_bar_language_german"),
            })
          : translation.translate("command_bar_pick_language"),
        options: [
          {
            label: translation.translate("command_bar_language_english"),
            onSelect: () => void applyLanguage("ENGLISH"),
            isSelected: userSettings.data?.app_language === "ENGLISH",
          },
          {
            label: translation.translate("command_bar_language_german"),
            onSelect: () => void applyLanguage("GERMAN"),
            isSelected: userSettings.data?.app_language === "GERMAN",
          },
        ],
      };
    }
    if (intent === "SET_MODEL") {
      const applyModel = async (value: "gpt-4o" | "gpt-4o-mini") => {
        try {
          setPending(translation.translate("command_bar_status_working"));
          await updateUserSettings.mutateAsync({
            userSettingsDto: { ai_model: value },
          });
          setSuccess(translation.translate("command_bar_status_done"));
        } catch {
          setError(translation.translate("command_bar_status_error"));
        }
      };
      const castValue = value as "gpt-4o" | "gpt-4o-mini" | undefined;
      return {
        intent: "SET_MODEL",
        title: castValue
          ? translation.translate("command_bar_confirm_model", {
              model:
                castValue === "gpt-4o-mini"
                  ? "GPT-4o mini"
                  : "GPT-4o",
            })
          : translation.translate("command_bar_pick_model"),
        options: [
          {
            label: "GPT-4o",
            onSelect: () => void applyModel("gpt-4o"),
            isSelected: userSettings.data?.ai_model === "gpt-4o",
          },
          {
            label: "GPT-4o mini",
            onSelect: () => void applyModel("gpt-4o-mini"),
            isSelected: userSettings.data?.ai_model === "gpt-4o-mini",
          },
        ],
      };
    }
    if (intent === "UPDATE_PROFILE_PIC") {
      return {
        intent: "UPDATE_PROFILE_PIC",
        title: translation.translate("command_bar_confirm_photo"),
        description: translation.translate("command_bar_photo_hint"),
        confirmLabel: translation.translate("command_bar_action_photo"),
        onConfirm: () => {
          profilePickerRef.current?.click();
        },
      };
    }
    if (intent === "LOGOUT") {
      return {
        intent: "LOGOUT",
        title: translation.translate("command_bar_confirm_logout"),
        confirmLabel: translation.translate("command_bar_action_logout"),
        onConfirm: () => {
          login.logoutMutation.mutate();
          setNavbarOpen(false);
        },
      };
    }
    return null;
  };

  const parsedCommand = useMemo(() => parseCommand(commandQuery), [commandQuery]);

  useEffect(() => {
    if (parsedCommand.intent !== "UNKNOWN") {
      setSelectedIntent(parsedCommand.intent as typeof selectedIntent);
    }
  }, [parsedCommand.intent]);

  const commandCandidate: CommandCandidate | null = useMemo(() => {
    if (parsedCommand.intent === "UNKNOWN") {
      return buildCandidate(selectedIntent);
    }
    if (parsedCommand.intent === selectedIntent) {
      return buildCandidate(parsedCommand.intent, parsedCommand.value);
    }
    return buildCandidate(selectedIntent);
  }, [
    parsedCommand,
    selectedIntent,
    commandQuery,
    dailyTextCreate,
    languageSpace,
    navigate,
    translation,
    updateUserSettings,
    login,
  ]);

  const commandItems = [
    {
      intent: "SET_UI_LANGUAGE" as const,
      label: translation.translate("command_bar_item_language"),
      keywords: ["sprache", "language", "app-sprache", "ui"],
    },
    {
      intent: "SET_MODEL" as const,
      label: translation.translate("command_bar_item_model"),
      keywords: ["modell", "model", "gpt"],
    },
    {
      intent: "UPDATE_PROFILE_PIC" as const,
      label: translation.translate("command_bar_item_photo"),
      keywords: ["profil", "profile", "foto", "photo", "bild"],
    },
    {
      intent: "START_DAILY_TEXT" as const,
      label: translation.translate("command_bar_item_daily"),
      keywords: ["text", "heute", "daily"],
    },
    {
      intent: "NAV_SETTINGS" as const,
      label: translation.translate("command_bar_item_settings"),
      keywords: ["einstellungen", "settings"],
    },
    {
      intent: "LOGOUT" as const,
      label: translation.translate("command_bar_item_logout"),
      keywords: ["abmelden", "logout", "sign out"],
    },
  ].filter((item) => item.label?.trim());

  const filteredCommandItems = useMemo(() => {
    const query = commandQuery.trim().toLowerCase();
    if (!query) return commandItems;
    return commandItems.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.keywords.some((keyword) => query.includes(keyword)),
    );
  }, [commandItems, commandQuery]);

  const visibleCommandItems =
    selectedIntent === "HOME"
      ? filteredCommandItems
      : filteredCommandItems.filter((item) => item.intent === selectedIntent);

  const handleProfileFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      if (profilePreviewUrl) {
        URL.revokeObjectURL(profilePreviewUrl);
      }
      setProfilePreviewUrl(null);
      event.target.value = "";
      return;
    }
    if (profilePreviewUrl) {
      URL.revokeObjectURL(profilePreviewUrl);
    }
    setProfilePreviewUrl(URL.createObjectURL(file));
    event.target.value = "";
    try {
      setCommandStatus({
        kind: "pending",
        message: translation.translate("command_bar_status_working"),
      });
      await updateProfile.mutateAsync({
        updateUserProfileDto: {
          pic: file,
        },
      });
      setCommandStatus({
        kind: "success",
        message: translation.translate("command_bar_status_done"),
      });
      notifySnackbar({
        message: translation.translate("command_bar_status_done"),
      });
    } catch {
      setCommandStatus({
        kind: "error",
        message: translation.translate("command_bar_status_error"),
      });
    }
  };

  const currentProfilePic = profilePicPath(loginStatus.data.userId);
  const handleNavbarToggle = () => {
    setNavbarOpen((prev) => {
      const next = !prev;
      if (next) {
        setLanguageMenuOpen(false);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col h-dvh overflow-hidden w-screen">
      <div className="relative z-40 flex h-16 items-center justify-between shadow-lg pr-4">
        <div className="flex items-center gap-2">
          <div
            data-testid="navbar-toggle"
            onClick={handleNavbarToggle}
            className="hover:cursor-pointer"
          >
            <CommandLineIcon className="mx-4 h-6 text-slate-800" />
          </div>
          {!isOnMainScreen && (
            <button
              type="button"
              aria-label="Go home"
              onClick={() => {
                navigate({ to: "/app/reeprise" });
              }}
              className={`-ml-3 inline-flex items-center justify-center rounded-md p-2 text-slate-800 transition-opacity duration-75 hover:bg-slate-100 ${homeVisibilityClass}`}
            >
              <HomeIcon className="h-6 w-6" />
            </button>
          )}
          <div
            className={`flex items-center gap-2 transition-opacity duration-75 ${navbarAuxVisibilityClass}`}
          >
            <div className="relative" ref={languageMenuRef}>
              <button
                type="button"
                aria-label={translation.translate("language_space_switch")}
                onClick={() => {
                  if (hideNavbarAuxControls) {
                    setNavbarOpen(false);
                    setLanguageMenuOpen(true);
                    return;
                  }
                  setLanguageMenuOpen((prev) => !prev);
                }}
                className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-base font-semibold text-slate-900 transition hover:bg-slate-50"
              >
                <span className="flex max-w-[160px] flex-col items-center text-center leading-tight">
                  <span className="truncate">{currentLanguageLabel}</span>
                  <span className="text-xs font-[TikTok] italic text-slate-500">
                    {translation.translate("language_switch_with_brand")}
                  </span>
                </span>
                <ChevronDownIcon
                  className={`h-4 w-4 text-slate-500 transition ${
                    languageMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {languageMenuOpen && (
                <div className="absolute left-0 mt-2 w-60 max-w-[calc(100vw-1rem)] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl z-50 max-h-[70vh] overflow-y-auto">
                  <div className="px-3 py-1 text-base uppercase tracking-wide text-slate-500">
                    {translation.translate("language_space_switch")}
                  </div>
                  <div className="mt-1 grid gap-1">
                    {languageOptions.map((opt) => {
                      const isActive = opt.id === languageSpace;
                      const isGreekTranscript =
                        opt.id === "GREEK_LATIN_TRANSCRIPTION";
                      const isRussianTranscript =
                        opt.id === "RUSSIAN_LATIN_TRANSCRIPTION";
                      const isPortugueseVariant =
                        opt.id === "PORTUGUESE_FROM_BRAZIL" ||
                        opt.id === "PORTUGUESE_FROM_PORTUGAL";
                      const optionLabel = isGreekTranscript
                        ? translation.translate("language_greek")
                        : isRussianTranscript
                          ? translation.translate("language_russian")
                        : isPortugueseVariant
                          ? translation.translate("language_portuguese")
                          : opt.label;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            setLanguageSpace(opt.id);
                            setLanguageMenuOpen(false);
                          }}
                          className="flex items-center justify-start gap-3 rounded-xl px-3 py-2 text-base font-semibold text-left transition"
                          style={{
                            backgroundColor: isActive ? "#e2e8f0" : "#f8fafc",
                            color: "#0f172a",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <span className="flex flex-col items-start leading-tight">
                            <span>{optionLabel}</span>
                            {(isGreekTranscript || isRussianTranscript) && (
                              <span className="text-base text-slate-500">
                                {translation.translate(
                                  "language_variant_latin_transcription",
                                )}
                              </span>
                            )}
                            {isPortugueseVariant && (
                              <span className="text-base text-slate-500">
                                {translation.translate(
                                  opt.id === "PORTUGUESE_FROM_BRAZIL"
                                    ? "language_variant_portuguese_brazil"
                                    : "language_variant_portuguese_portugal",
                                )}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={`flex items-center gap-3 h-16 pr-4 transition-opacity duration-75 ${navbarAuxVisibilityClass}`}
        >
          <div className="flex items-center gap-3 px-2 py-1">
            <ProgressBar />
            <div className="h-6 w-px bg-slate-200" />
          </div>
          <Link
            to="/app/profile"
            className="inline-flex h-9 w-9 items-center justify-center"
          >
            <UserIcon className="w-5 h-5 text-slate-700" />
          </Link>
        </div>
      </div>

      <div className="flex flex-1 overflow-scroll">
        <div
          className={`transition-all absolute flex
          z-60 h-full w-full transform md:relative overflow-y-auto 
          ${navbarOpen ? "translate-x-0 md:w-[440px]" : "-translate-x-full md:translate-x-0 md:w-0 pointer-events-none"}`}
        >
          <div className="bg-white w-full md:w-[440px] h-full border-r border-slate-100">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 pt-4 pb-3 backdrop-blur">
              <p className="text-base font-semibold uppercase tracking-wide text-slate-500">
                {translation.translate("command_bar_title")}
              </p>
              <div className="mt-2 flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                <CommandLineIcon className="h-4 w-4 text-slate-400" />
                <input
                  value={commandQuery}
                  onChange={(event) => setCommandQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      commandCandidate &&
                      !commandCandidate.options?.length
                    ) {
                      event.preventDefault();
                      commandCandidate.onConfirm?.();
                    }
                  }}
                  placeholder={
                    commandQuery
                      ? ""
                      : translation.translate("command_bar_placeholder")
                  }
                  className="w-full bg-transparent text-base text-slate-800 placeholder:text-slate-400 focus:outline-none"
                />
              </div>
              <div
                className={`mt-4 space-y-3 md:space-y-0 md:grid md:gap-3 ${
                  selectedIntent === "HOME"
                    ? "md:grid-cols-[1fr_1.2fr]"
                    : "md:grid-cols-1"
                }`}
              >
                <div className="rounded-2xl bg-white">
                  <div className="px-3 pt-3 pb-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      aria-label="Home"
                      onClick={() => setSelectedIntent("HOME")}
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-base font-semibold ${
                        selectedIntent === "HOME"
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      🏠
                    </button>
                    <div className="flex flex-1 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedIntent("HOME")}
                        className={`text-base font-semibold uppercase tracking-wide ${
                          selectedIntent === "HOME"
                            ? "text-slate-800"
                            : "text-slate-400"
                        }`}
                      >
                        Home
                      </button>
                      {selectedIntent !== "HOME" && (
                        <>
                          <span className="text-base text-slate-300">/</span>
                          <span className="text-base font-semibold uppercase tracking-wide text-slate-800">
                            {commandItems.find(
                              (item) => item.intent === selectedIntent,
                            )?.label ?? ""}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="px-2 pb-3 space-y-1">
                    {visibleCommandItems.map((item) => {
                      const isActive = item.intent === selectedIntent;
                      return (
                        <div key={item.intent} className="space-y-2">
                          {selectedIntent === "HOME" && (
                            <button
                              type="button"
                              onClick={() => {
                                if (item.intent === "NAV_SETTINGS") {
                                  navigate({ to: "/app/settings" });
                                  return;
                                }
                                setSelectedIntent(item.intent);
                              }}
                              className={`w-full text-left rounded-xl px-3 py-2 text-base font-semibold transition ${
                                isActive
                                  ? "bg-slate-900 text-white"
                                  : "text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              {item.label}
                            </button>
                          )}
                          {isActive && commandCandidate && (
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                              <p className="text-base font-semibold text-slate-700">
                                {commandCandidate.title}
                              </p>
                                  {commandCandidate.description && (
                                    <p className="text-base text-slate-500 mt-1">
                                      {commandCandidate.description}
                                    </p>
                                  )}
                              {commandCandidate.intent ===
                                "UPDATE_PROFILE_PIC" && (
                                <div className="mt-3">
                                  <div className="relative inline-flex">
                                    {profilePreviewUrl ? (
                                      <img
                                        src={profilePreviewUrl}
                                        alt="Selected profile preview"
                                        className="h-20 w-20 rounded-full object-cover"
                                      />
                                    ) : currentProfilePic ? (
                                      <img
                                        src={currentProfilePic}
                                        alt="Current profile"
                                        className="h-20 w-20 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                                        <UserIcon className="h-8 w-8" />
                                      </div>
                                    )}
                                    <div className="absolute bottom-0 right-0 h-7 w-7 rounded-full border-2 border-white bg-blue-500 shadow flex items-center justify-center">
                                      <button
                                        type="button"
                                        aria-label={translation.translate(
                                          "command_bar_action_photo",
                                        )}
                                        onClick={() =>
                                          profilePickerRef.current?.click()
                                        }
                                      >
                                        <PlusIcon className="h-4 w-4 text-white" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {commandCandidate.options?.length ? (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {commandCandidate.options
                                    .filter((option) => option.label?.trim())
                                    .map((option) => (
                                      <button
                                        key={option.label}
                                        type="button"
                                        onClick={option.onSelect}
                                        className={`rounded-full border px-3 py-1 text-base font-semibold transition ${
                                          option.isSelected
                                            ? "border-slate-900 bg-slate-900 text-white"
                                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                                        }`}
                                      >
                                        {option.label}
                                      </button>
                                    ))}
                                </div>
                              ) : null}
                              {!commandCandidate.options?.length &&
                                commandCandidate.onConfirm &&
                                commandCandidate.confirmLabel &&
                                commandCandidate.intent !== "UPDATE_PROFILE_PIC" && (
                                  <div className="mt-4 flex items-center gap-2">
                                    <button
                                      type="button"
                                      className={`flex items-center gap-2 px-4 py-2 text-base font-semibold transition ${
                                        commandCandidate.intent ===
                                        "NAV_SETTINGS"
                                          ? "w-full justify-between rounded-xl border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                                          : "rounded-full bg-slate-900 text-white hover:bg-slate-800"
                                      }`}
                                      onClick={commandCandidate.onConfirm}
                                    >
                                      {commandCandidate.confirmLabel}
                                      {commandCandidate.intent ===
                                        "NAV_SETTINGS" && (
                                        <ChevronRightIcon className="h-4 w-4 text-slate-500" />
                                      )}
                                    </button>
                                  </div>
                                )}
                              {commandStatus.message &&
                                commandStatus.kind !== "success" && (
                                  <p
                                    className={`mt-2 text-base ${
                                      commandStatus.kind === "error"
                                        ? "text-rose-600"
                                        : "text-slate-500"
                                    }`}
                                  >
                                    {commandStatus.message}
                                  </p>
                                )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {filteredCommandItems.length === 0 && (
                    <p className="px-4 pb-4 text-base text-slate-500">
                      {translation.translate("command_bar_unknown")}
                    </p>
                  )}
                </div>

                {selectedIntent === "HOME" && commandCandidate && (
                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm">
                    <p className="text-base font-semibold text-slate-900">
                      {commandCandidate.title}
                    </p>
                    {commandCandidate.description && (
                      <p className="text-base text-slate-500 mt-1">
                        {commandCandidate.description}
                      </p>
                    )}
                    {commandCandidate.options?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {commandCandidate.options
                          .filter((option) => option.label?.trim())
                          .map((option) => (
                            <button
                              key={option.label}
                              type="button"
                              onClick={option.onSelect}
                              className={`rounded-full border px-3 py-1 text-base font-semibold transition ${
                                option.isSelected
                                  ? "border-slate-900 bg-slate-900 text-white"
                                  : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                      </div>
                    ) : null}
                    {!commandCandidate.options?.length &&
                      commandCandidate.onConfirm &&
                      commandCandidate.confirmLabel && (
                        <div className="mt-4 flex items-center gap-2">
                          <button
                            type="button"
                            className={`flex items-center gap-2 px-4 py-2 text-base font-semibold transition ${
                              commandCandidate.intent === "NAV_SETTINGS"
                                ? "w-full justify-between rounded-xl border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                                : "rounded-full bg-slate-900 text-white hover:bg-slate-800"
                            }`}
                            onClick={commandCandidate.onConfirm}
                          >
                            {commandCandidate.confirmLabel}
                            {commandCandidate.intent === "NAV_SETTINGS" && (
                              <ChevronRightIcon className="h-4 w-4 text-slate-500" />
                            )}
                          </button>
                        </div>
                      )}
                    {commandStatus.message && (
                      <p
                        className={`mt-2 text-base ${
                          commandStatus.kind === "error"
                            ? "text-rose-600"
                            : commandStatus.kind === "success"
                              ? "text-emerald-600"
                              : "text-slate-500"
                        }`}
                      >
                        {commandStatus.message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="px-2 py-3 space-y-1">
              {(
                [
                  {
                    link: "/",
                    text: "uxhulu.com",
                    icon: <ArrowUpRightIcon className="w-5 h-5" />,
                  } as NavItem,
                loginStatus.data.permissions.includes(BARKS_VIEW_PERMISSION)
                  ? ({
                      link: "/app/bark_classic",
                      text: "Classic Barkguide",
                      icon: <MapIcon className="w-5 h-5" />,
                    } as NavItem)
                  : undefined,
                loginStatus.data.permissions.includes(
                  SHOPPING_LIST_VIEW_PERMISSION,
                )
                  ? ({
                      link: "/app/shoppinglist",
                      text: "Shopping List",
                      icon: <ShoppingCartIcon className="w-5 h-5" />,
                    } as NavItem)
                  : undefined,
                show_experimental
                  ? ({
                      link: "/app/account",
                      text: "Bark Account",
                      icon: <BanknotesIcon className="w-5 h-5" />,
                    } as NavItem)
                  : undefined,
                {
                  link: "/app/reeprise",
                  text: translation.translate("sidenav_texts"),
                  icon: <BookOpenIcon className="w-5 h-5" />,
                } as NavItem,
                show_experimental
                  ? ({
                      link: "/app/check_your_email",
                      text: "Email",
                      icon: <EnvelopeIcon className="w-5 h-5" />,
                    } as NavItem)
                  : undefined,
                {
                  link: "/app/vocabulary_trainer",
                  text: translation.translate("link_vocabulary_list"),
                  icon: <ClipboardDocumentListIcon className="w-5 h-5" />,
                } as NavItem,
                {
                  link: "/app/vocabulary_progress",
                  text: translation.translate("link_vocabulary_progress"),
                  icon: <ChartBarIcon className="w-5 h-5" />,
                } as NavItem,
                {
                  link: "/app/product_logbook",
                  text: "Release Notes",
                  icon: <InformationCircleIcon className="w-5 h-5" />,
                } as NavItem,
                show_experimental
                  ? ({
                      link: "/app/vocabulary_trainer_new",
                      text: "New Trainer",
                      icon: <AcademicCapIcon className="w-5 h-5" />,
                    } as NavItem)
                  : undefined,
                show_experimental
                  ? ({
                      link: "/app/dialogue",
                      text: "Dialogue",
                      icon: <AcademicCapIcon className="w-5 h-5" />,
                    } as NavItem)
                  : undefined,
                {
                  link: "/app/statistics",
                  text: translation.translate("sidenav_statistics"),
                  icon: <ChartBarIcon className="w-5 h-5" />,
                } as NavItem,
                {
                  link: "/app/interest_map",
                  text: translation.translate("sidenav_interest_map"),
                  icon: <MapIcon className="w-5 h-5" />,
                } as NavItem,
                {
                  link: "/app/settings",
                  text: translation.translate("sidenav_settings"),
                  icon: <Cog8ToothIcon className="w-5 h-5" />,
                } as NavItem,
              ] as (NavItem | undefined)[]
            )
              .filter((obj): obj is NavItem => obj !== undefined)
              .map((obj) => (
                <Link
                  key={obj.link}
                  to={obj.link}
                  onClick={() => setNavbarOpen(false)}
                >
                  <div className="flex items-center gap-3 rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition">
                    {obj.icon}
                    <span>{obj.text}</span>
                  </div>
                </Link>
              ))}
            {loginStatus.data.is_staff && (
              <a key="/admin" href="/admin">
                <div className="flex items-center gap-3 rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition">
                  <KeyIcon className="w-5 h-5" />
                  <span>{translation.translate("sidenav_admin_area")}</span>
                </div>
              </a>
            )}
            <div
              data-testid="logout-button"
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-base font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer"
              onClick={() => login.logoutMutation.mutate()}
            >
              <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
              <span>{translation.translate("logout_button")}</span>
            </div>
            </div>
          </div>
        </div>
        <div
          className={`absolute md:hidden w-screen  h-full bg-slate-900 transition-all z-50 ${navbarOpen ? "opacity-15" : "opacity-0 pointer-events-none"}`}
          onClick={() => {
            handleNavbarToggle();
          }}
        />
        <div data-app-content-scroll className="flex-1 bg-neutral-50 overflow-scroll h-full">
          <Outlet />
        </div>
      </div>
      <input
        ref={profilePickerRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleProfileFileChange}
      />
      <SnackBar />
    </div>
  );
}
